import { api } from "@/magazzino/lib/apiClient";
import { printHtmlDirect } from "@/lib/directPrint";

export async function printDDT(movementId) {
  const { data } = await api.get(`/movimenti/${movementId}/ddt`);
  const { movement: m, product: p, company: c, supplier: s, ddt_number } = data;
  const isIn = m.type === "in";
  const title = isIn ? "Bolla di Carico" : "Bolla di Scarico";
  const dt = new Date(m.created_at);
  const dateStr = dt.toLocaleDateString("it-IT");
  const timeStr = dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

  const esc = (v) => String(v ?? "").replace(/[&<>]/g, (ch) => ({"&":"&amp;","<":"&lt;",">":"&gt;"}[ch]));

  const html = `<!doctype html><html><head><meta charset="utf-8"/>
  <title>${esc(title)} — ${esc(ddt_number)}</title>
  <link href="https://api.fontshare.com/v2/css?f[]=cabinet-grotesk@800,700,500,400&f[]=ibm-plex-sans@600,500,400&display=swap" rel="stylesheet"/>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'IBM Plex Sans', system-ui, sans-serif; color:#0f172a; padding:32px; max-width: 820px; margin:auto; }
    h1, .display { font-family:'Cabinet Grotesk', sans-serif; letter-spacing:-.02em; }
    .header { display:flex; justify-content:space-between; align-items:flex-start; padding-bottom:20px; border-bottom:2px solid #0f172a; }
    .company-block { display:flex; gap:14px; align-items:center; }
    .logo { width:64px; height:64px; object-fit:contain; }
    .company-name { font-size:20px; font-weight:700; }
    .company-info { font-size:11px; color:#475569; line-height:1.5; }
    .doc-info { text-align:right; }
    .doc-type { font-size:11px; text-transform:uppercase; letter-spacing:.15em; color:#64748b; }
    .doc-title { font-size:26px; font-weight:800; margin:4px 0; }
    .doc-num { font-size:12px; color:#334155; }
    .badge { display:inline-block; padding:3px 10px; border-radius:4px; font-size:11px; font-weight:600; margin-top:6px;
             background:${isIn ? "#dcfce7" : "#ffedd5"}; color:${isIn ? "#166534" : "#9a3412"}; }
    section { margin-top:26px; }
    .sec-title { font-size:10px; text-transform:uppercase; letter-spacing:.15em; color:#64748b; margin-bottom:8px; }
    .grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:24px; }
    .box { border:1px solid #e2e8f0; border-radius:6px; padding:14px; font-size:12px; line-height:1.6; }
    table { width:100%; border-collapse:collapse; margin-top:8px; font-size:12px; }
    th { text-align:left; padding:10px 12px; background:#f1f5f9; text-transform:uppercase; font-size:10px; letter-spacing:.1em; color:#334155; }
    td { padding:12px; border-bottom:1px solid #e2e8f0; }
    .total-row td { font-weight:700; background:#f8fafc; }
    .footer { margin-top:60px; display:grid; grid-template-columns:1fr 1fr; gap:40px; }
    .sign { border-top:1px solid #0f172a; padding-top:6px; font-size:11px; color:#475569; text-align:center; }
    .meta { margin-top:36px; font-size:10px; color:#94a3b8; text-align:center; }
    @media print { body { padding:0; } }
  </style></head><body>
    <div class="header">
      <div class="company-block">
        ${c.logo_url ? `<img class="logo" src="${esc(c.logo_url)}" alt="logo"/>` : ''}
        <div>
          <div class="company-name display">${esc(c.name || "La Tua Azienda")}</div>
          <div class="company-info">
            ${esc(c.address || "")}${c.city ? ", " + esc(c.city) : ""}<br/>
            ${c.vat ? "P.IVA " + esc(c.vat) : ""}${c.phone ? " · Tel " + esc(c.phone) : ""}<br/>
            ${esc(c.email || "")}
          </div>
        </div>
      </div>
      <div class="doc-info">
        <div class="doc-type">Documento di Trasporto</div>
        <div class="doc-title display">${esc(title)}</div>
        <div class="doc-num">N° <b>${esc(ddt_number)}</b></div>
        <div class="doc-num">Data: <b>${dateStr}</b> · ${timeStr}</div>
        <div class="badge">${isIn ? "ENTRATA MERCE" : "USCITA MERCE"}</div>
      </div>
    </div>

    <section>
      <div class="grid-2">
        <div>
          <div class="sec-title">${isIn ? "Fornitore / Mittente" : "Destinazione / Uscita"}</div>
          <div class="box">
            ${isIn && s
              ? `<b>${esc(s.name)}</b><br/>${esc(s.address || "")}<br/>${esc(s.email || "")}${s.phone ? " · " + esc(s.phone) : ""}`
              : `<b>${esc(m.reference || "—")}</b><br/><span style="color:#64748b">${esc(m.note || "Vedi note movimento")}</span>`
            }
          </div>
        </div>
        <div>
          <div class="sec-title">Riferimenti</div>
          <div class="box">
            <b>Rif. documento:</b> ${esc(m.reference || "—")}<br/>
            <b>Operatore:</b> ${esc(m.user_name || "—")}<br/>
            <b>Note:</b> ${esc(m.note || "—")}
          </div>
        </div>
      </div>
    </section>

    <section>
      <div class="sec-title">Merce trasportata</div>
      <table>
        <thead><tr>
          <th>SKU</th><th>Descrizione articolo</th><th style="text-align:right">Q.tà</th><th>U.M.</th>
        </tr></thead>
        <tbody>
          <tr>
            <td>${esc(p.sku || "")}</td>
            <td>
              <b>${esc(p.name || "")}</b>
              ${p.description ? `<div style="color:#64748b;font-size:11px;margin-top:2px">${esc(p.description)}</div>` : ""}
            </td>
            <td style="text-align:right"><b>${m.quantity}</b></td>
            <td>${esc(p.unit || "pz")}</td>
          </tr>
          <tr class="total-row">
            <td colspan="2" style="text-align:right">TOTALE COLLI</td>
            <td style="text-align:right">${m.quantity}</td>
            <td>${esc(p.unit || "pz")}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <div class="footer">
      <div class="sign">Firma conducente</div>
      <div class="sign">Firma destinatario</div>
    </div>

    <div class="meta">Documento generato automaticamente da MagazzinoPro il ${new Date().toLocaleString("it-IT")}</div>
  </body></html>`;
  printHtmlDirect(html, `${title} ${ddt_number}`);
}

export async function printMultipleDDT(movementIds) {
  const ids = [...new Set(movementIds || [])].filter(Boolean);
  if (!ids.length) throw new Error("Seleziona almeno uno scarico");

  const responses = await Promise.all(
    ids.map((movementId) => api.get(`/movimenti/${movementId}/ddt`))
  );
  const documents = responses.map((response) => response.data);
  const first = documents[0];
  const destination = String(first.movement?.reference || "").trim();
  if (documents.some(({ movement }) => movement?.type !== "out")) {
    throw new Error("Il DDT multiplo può contenere soltanto movimenti di scarico");
  }
  if (documents.some(({ movement }) => String(movement?.reference || "").trim() !== destination)) {
    throw new Error("Seleziona scarichi con la stessa destinazione");
  }

  const esc = (value) => String(value ?? "").replace(/[&<>]/g, (character) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[character]));
  const company = first.company || {};
  const movement = first.movement || {};
  const date = new Date(movement.created_at);
  const dateString = date.toLocaleDateString("it-IT");
  const timeString = date.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
  const rows = documents.map(({ movement: item, product }) => `
    <tr>
      <td>${esc(product?.sku || "")}</td>
      <td><b>${esc(product?.name || "")}</b>${product?.description ? `<div class="description">${esc(product.description)}</div>` : ""}</td>
      <td class="right"><b>${esc(item?.quantity || 0)}</b></td>
      <td>${esc(product?.unit || "pz")}</td>
    </tr>`).join("");
  const notes = [...new Set(documents.map(({ movement: item }) => String(item?.note || "").trim()).filter(Boolean))].join(" · ");

  const html = `<!doctype html><html><head><meta charset="utf-8"/>
  <title>Bolla di Scarico — ${esc(first.ddt_number)}</title>
  <style>
    *{box-sizing:border-box} body{font-family:Arial,sans-serif;color:#0f172a;padding:32px;max-width:820px;margin:auto}
    .header{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:2px solid #0f172a}
    .company{font-size:20px;font-weight:700}.info{font-size:11px;color:#475569;line-height:1.5}.doc{text-align:right}
    .type,.section-title{font-size:10px;text-transform:uppercase;letter-spacing:.15em;color:#64748b}.title{font-size:26px;font-weight:800;margin:4px 0}
    .number{font-size:12px;color:#334155}.badge{display:inline-block;margin-top:6px;padding:3px 10px;border-radius:4px;background:#ffedd5;color:#9a3412;font-size:11px;font-weight:600}
    section{margin-top:26px}.grid{display:grid;grid-template-columns:1fr 1fr;gap:24px}.box{border:1px solid #e2e8f0;border-radius:6px;padding:14px;font-size:12px;line-height:1.6}
    table{width:100%;border-collapse:collapse;margin-top:8px;font-size:12px}th{text-align:left;padding:10px 12px;background:#f1f5f9;text-transform:uppercase;font-size:10px;letter-spacing:.1em;color:#334155}td{padding:12px;border-bottom:1px solid #e2e8f0}.right{text-align:right}.description{color:#64748b;font-size:11px;margin-top:2px}
    .footer{margin-top:60px;display:grid;grid-template-columns:1fr 1fr;gap:40px}.sign{border-top:1px solid #0f172a;padding-top:6px;font-size:11px;color:#475569;text-align:center}.meta{margin-top:36px;font-size:10px;color:#94a3b8;text-align:center}
    @media print{body{padding:0}}
  </style></head><body>
    <div class="header"><div><div class="company">${esc(company.name || "La Tua Azienda")}</div><div class="info">${esc(company.address || "")}${company.city ? `, ${esc(company.city)}` : ""}<br/>${company.vat ? `P.IVA ${esc(company.vat)}` : ""}${company.phone ? ` · Tel ${esc(company.phone)}` : ""}<br/>${esc(company.email || "")}</div></div>
    <div class="doc"><div class="type">Documento di Trasporto</div><div class="title">Bolla di Scarico</div><div class="number">N° <b>${esc(first.ddt_number)}</b></div><div class="number">Data: <b>${dateString}</b> · ${timeString}</div><div class="badge">USCITA MERCE</div></div></div>
    <section><div class="grid"><div><div class="section-title">Destinazione / Uscita</div><div class="box"><b>${esc(destination || "—")}</b><br/>${esc(notes || "Vedi righe del documento")}</div></div><div><div class="section-title">Riferimenti</div><div class="box"><b>Operatore:</b> ${esc(movement.user_name || "—")}<br/><b>Movimenti inclusi:</b> ${documents.length}<br/><b>Note:</b> ${esc(notes || "—")}</div></div></div></section>
    <section><div class="section-title">Merce trasportata</div><table><thead><tr><th>SKU</th><th>Descrizione articolo</th><th class="right">Q.tà</th><th>U.M.</th></tr></thead><tbody>${rows}</tbody></table></section>
    <div class="footer"><div class="sign">Firma conducente</div><div class="sign">Firma destinatario</div></div>
    <div class="meta">Documento generato automaticamente da MagazzinoPro il ${new Date().toLocaleString("it-IT")}</div>
  </body></html>`;
  printHtmlDirect(html, `Bolla di Scarico ${first.ddt_number}`);
}
